import metrics from './init-metrics';
import { replaceBaseParams } from './metrics-context';
import { IBid } from './provider';

type VideoBidMeta = { bidder?: string; cpm?: number; url?: string };
type ErrorEntry = {
    attempt: number;
    player: string;
    bidder?: string;
    cpm?: number;
    code?: string | number;
    message: string;
    url?: string;
    type?: string;
    vastErrorCode?: number;
    innerError?: string;
};

let videoErrors: ErrorEntry[] = [];

const buildErrorString = (entry: ErrorEntry) => {
    const parts = [
        `#${entry.attempt}`,
        entry.player ? `[${entry.player}]` : '[Unknown]',
        entry.bidder ? `bidder=${entry.bidder}` : '',
        entry.cpm != null ? `cpm=${entry.cpm}` : '',
        entry.url ? `url=${entry.url}` : '',
        entry.code ? `code=${entry.code}` : '',
        entry.vastErrorCode ? `vastCode=${entry.vastErrorCode}` : '',
        entry.type ? `type=${entry.type}` : '',
        entry.innerError ? `inner=${entry.innerError}` : '',
        entry.message
    ].filter(Boolean);

    return parts.join(' | ');
};

export const VideoReporter = {
    reset() {
        videoErrors = [];
    },

    setBidderContext(bid: IBid) {
        this.reset();
        replaceBaseParams({
            bidder: bid.provider.name,
            cpm: bid.bid.cpm ? String(bid.bid.cpm) : undefined,
            adBidder: bid.provider.name,
            adCpm: bid.bid.cpm ? String(bid.bid.cpm) : undefined
        });
    },

    handleError(
        e: any,
        details: { player?: string } | undefined,
        currentRetryCount: number,
        remainingBids?: IBid[],
        currentBid?: VideoBidMeta
    ) {
        // Unwrap IMA AdErrorEvent or similar wrappers
        const innerError = e?.getError?.() ?? e;

        const errorCode = innerError?.getErrorCode?.() ?? innerError?.errorCode ?? innerError?.code;
        const message = innerError?.getMessage?.() ?? innerError?.message ?? 'Unknown video error';
        const type = innerError?.getType?.();
        const vastErrorCode = innerError?.getVastErrorCode?.();

        // Try to get inner error message if available
        let innerErrorMsg: string | undefined;
        try {
            const nestedError = innerError?.getInnerError?.();
            if (nestedError) {
                innerErrorMsg = nestedError.message || String(nestedError);
            }
        } catch (ignore) {
            // ignore if getInnerError fails
        }

        const player = details?.player ?? 'Unknown';

        const nextRetryCount = currentRetryCount + 1;
        const willRetry = remainingBids && remainingBids.length > 0;

        const entry: ErrorEntry = {
            attempt: nextRetryCount,
            player,
            bidder: currentBid?.bidder,
            cpm: currentBid?.cpm,
            url: currentBid?.url,
            code: errorCode,
            message,
            type,
            vastErrorCode,
            innerError: innerErrorMsg
        };

        videoErrors.push(entry);
        const videoErrorString = videoErrors.map(buildErrorString).join(' || ');
        const videoErrorTrace = JSON.stringify(videoErrors);

        // Build a single payload because Jarvis overwrites params on every call
        const params: Record<string, string> = {
            videoError: videoErrorString,
            videoErrorTrace,
            adError: videoErrorString,
            adErrorTrace: videoErrorTrace,
            player,
            adPlayer: player,
            videoRetryCount: String(nextRetryCount),
            adRetryCount: String(nextRetryCount),
            finish_reason: "error",
            ad_stage: "error"
        };
        if (typeof errorCode !== 'undefined') {
            params.videoErrorCode = String(errorCode);
            params.adErrorCode = String(errorCode);
        }

        // Keep failing bid info in tags; if there is a next bid, switch bidder/cpm to it but keep the error trace
        if (willRetry) {
            const nextBid = remainingBids![0];
            params.bidder = nextBid.provider.name;
            params.adBidder = nextBid.provider.name;
            if (nextBid.bid.cpm != null) params.adCpm = String(nextBid.bid.cpm);
        } else if (currentBid?.bidder) {
            params.bidder = currentBid.bidder;
            params.adBidder = currentBid.bidder;
            if (currentBid.cpm != null) params.adCpm = String(currentBid.cpm);
        }

        replaceBaseParams(params);

        if (!willRetry) {
            metrics.trackRenderComplete('video-error');
        }
    }
};
